import { Button, CloseButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { IBlockReportSettings } from '../../../../data/providers/settings/reports/blockReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import {
  ICrossTestMetricSelection,
  ISelectableEntity,
  ISelectableMetric,
} from '../../../../data/types';
import {
  createBlockTemplate,
  updateBlockTemplate,
} from '../../../../data/writers/settings/reports/blockReportSettings';
import { queryHashGenerator } from '../../../../utils/queryHash';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ErrorMessage } from '../../../shared/form/ErrorMessage';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { MetricSelector } from '../../utils/settings/MetricSelector';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateMetadata } from '../../utils/settings/TemplateMetadata';
import { ITemplateFormProps } from '../../utils/settings/types';

export const TemplateForm = (props: ITemplateFormProps): React.ReactElement => {
  const [template, setTemplate] = React.useState<
    Partial<ISettingsTemplate<IBlockReportSettings>>
  >(props.template as ISettingsTemplate<IBlockReportSettings>);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  /**
   * @description handles change to the currently selected template's description
   * @param description
   */
  const templateDescriptionChangeHandler = (description: string): void => {
    setTemplate({
      ...template,
      description,
    });
  };

  /**
   * @description handles change to the currently selected template's name
   * @param name
   */
  const templateNameChangeHandler = (name: string): void => {
    setTemplate({
      ...template,
      name,
    });
  };

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (props.template && props.template?.id) {
        await updateBlockTemplate(
          template as ISettingsTemplate<IBlockReportSettings>,
          props.isSelected
        );
      } else {
        await createBlockTemplate(template);
      }

      props.onClose();
    } catch (e) {
      setLoading(false);
      setError('Something went wrong saving the template.');
    }
  };

  /**
   * @description handles changes to selected metrics
   * @param mMetrics
   */
  const metricsChangeHandler = (
    mMetrics: ICrossTestMetricSelection[]
  ): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        metrics: mMetrics.map(
          (
            m: ICrossTestMetricSelection
          ): {
            testType: ISelectableEntity;
            metric: ISelectableMetric;
            tags: ISelectableEntity[];
            queryHash: string;
            changeCutoffs?: {
              lowerCutoff: number;
              upperCutoff: number;
            };
          } => {
            return {
              testType: m.testType,
              metric: m.metric,
              tags: m.tags,
              queryHash: queryHashGenerator(
                m.testType.entity.id,
                m.tags.map((t: ISelectableEntity): string => t.entity.id)
              ),
              changeCutoffs: {
                lowerCutoff: m.changeCutoffs?.lowerCutoff ?? null,
                upperCutoff: m.changeCutoffs?.upperCutoff ?? null,
              },
            };
          }
        ),
      },
    });
  };

  const isValid = React.useMemo((): boolean => {
    if (isEmpty(template?.name)) return false;
    if (isEmpty(template?.settings)) return false;
    if (isEmpty(template?.settings?.metrics)) return false;
    if (
      template.settings.metrics.length < 0 ||
      template.settings.metrics.length > 5
    ) {
      return false;
    }

    return true;
  }, [JSON.stringify(template)]);

  return (
    <InnerPopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>
              {props.template
                ? `Edit ${props.template.name}`
                : 'Create Profile Settings Template'}
            </h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <Row shouldCollapse>
            <LeftColumn>
              <TemplateMetadata
                step={1}
                name={template?.name}
                description={template?.description}
                onTemplateDescriptionChange={templateDescriptionChangeHandler}
                onTemplateNameChange={templateNameChangeHandler}
              />
            </LeftColumn>
            <RightColumn>
              <MetricSelector
                min={1}
                max={5}
                onMetricsChange={metricsChangeHandler}
                metrics={template?.settings?.metrics ?? []}
                step={2}
                useCutoffs
                excludeAsymmetry
                isBlockReport
              />
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
